import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as d3 from "d3";
import { compose, prop, pluck, propEq } from "ramda";
import timelineXAxisLabels from "./timelineXAxisLabels";
import { getPeriod, samePeriod } from "utils/periods";
import { extractValue } from "utils/measurement";
import { format } from "utils/format";
import { targetColor } from "utils/colors";
import { translateDateToPeriod } from "utils/periods";
import { filterTicks } from "utils/dates";
import { convertSvgAttribute } from "utils/convertSvgAttribute";
export function initGraph(ref, setSvgHeight, margin) {
  var svg = d3.select(ref);
  var width = convertSvgAttribute(svg.style("width"));
  var svgHeight = setSvgHeight(svg);
  svg.append("g").attr("class", "xGrid").attr("transform", "translate(".concat([-width, margin.top], ")"));
  svg.append("g").attr("class", "yGrid").attr("transform", "translate(".concat([-width, margin.top], ")"));
  svg.append("g").attr("class", "gridSurfaces").attr("transform", "translate(".concat([margin.left, margin.top], ")"));
  svg.append("g").attr("class", "xAxis rotateLabels").attr("transform", "translate(".concat([0, svgHeight + margin.bottom], ")"));
  svg.append("g").attr("class", "yAxis").attr("pointer-events", "none").attr("transform", "translate(".concat([width + margin.right, margin.top], ")"));
  svg.append("g").attr("class", "chartData");
  svg.append("g").attr("class", "hover-description-group");
  var description = svg.append("g").attr("class", "tooltip").attr("transform", "translate(".concat([0, 0], ")")).attr("opacity", 0).attr("pointer-events", "none");
  description.append("rect").attr("width", 220).attr("height", 70).attr("stroke-width", 3).attr("fill", "white");
  description.append("text").attr("class", "value").attr("font-weight", "600");
  description.append("text").attr("class", "date").attr("font-size", "13px").attr("fill", "#000000").attr("transform", "translate(".concat([12, 38], ")"));
  description.append("text").attr("class", "organization").attr("font-size", "15px").attr("transform", "translate(".concat([12, 56], ")"));
  return description;
}
export function updateLines(_ref) {
  var content = _ref.content,
    measurementsWithMetadata = _ref.measurementsWithMetadata,
    key = _ref.key,
    valueline = _ref.valueline,
    inverse = _ref.inverse,
    ie11SafeLength = _ref.ie11SafeLength,
    genderDash = _ref.genderDash,
    color = _ref.color,
    unitlistIdx = _ref.unitlistIdx,
    margin = _ref.margin;
  var t = d3.transition().ease(d3.easeQuad).duration(400);
  var selection = content.selectAll("path").data(measurementsWithMetadata, key);
  var created = selection.enter().append("path").attr("class", "line").attr("transform", "translate(".concat([margin.left, margin.top], ")")).attr("d", compose(valueline, prop("measurements"))).attr("stroke-dashoffset", compose(inverse, ie11SafeLength)).transition(t).attr("stroke-dashoffset", 0);
  selection.merge(created).attr("d", compose(valueline, prop("measurements"))).style("stroke", function (_ref2) {
    var _color;
    var organizationId = _ref2.organizationId;
    return ((_color = color(unitlistIdx(organizationId), organizationId)) === null || _color === void 0 ? void 0 : _color.hex) || "#ffffff00";
  }).attr("stroke-dasharray", genderDash);
  selection.exit().style("opacity", 1).transition().ease(d3.easeQuad).duration(400).style("opacity", 0).remove();
}
export function updateXAxis(_ref3) {
  var svg = _ref3.svg,
    xScale = _ref3.xScale,
    cappedTicks = _ref3.cappedTicks,
    measurementPeriods = _ref3.measurementPeriods,
    periodType = _ref3.periodType,
    dateFrom = _ref3.dateFrom,
    dateTo = _ref3.dateTo,
    margin = _ref3.margin;
  var height = convertSvgAttribute(svg.style("height"));
  var singlePeriod = periodType && dateFrom && dateTo && samePeriod(getPeriod(periodType, dateFrom), getPeriod(periodType, dateTo));
  var cutLabel = function cutLabel(i) {
    return singlePeriod && i === 1;
  };
  var tickValues = (measurementPeriods === null || measurementPeriods === void 0 ? void 0 : measurementPeriods.length) > 0 && filterTicks(pluck("end", measurementPeriods), periodType, cappedTicks) || [];
  var forceYearLabel = tickValues.filter(function (x) {
    return getPeriod(periodType, x).start.getUTCMonth() === 0;
  }).length === 0;
  var calleeX = d3.axisBottom(xScale).tickValues(tickValues).tickSize(0).tickPadding(5).tickFormat(function (d, i) {
    return timelineXAxisLabels(d, periodType, cutLabel(i), singlePeriod || i === 0 && forceYearLabel);
  });
  svg.select(".xAxis").transition().ease(d3.easeQuad).duration(400).attr("transform", "translate(".concat([margin.left, height - margin.bottom], ")")).call(calleeX);
  svg.selectAll(".rotateLabels text").attr("transform", "translate(0) rotate(45 0 0)").style("shape-rendering", "crispedges").style("text-anchor", "start");
  svg.selectAll(".xAxis").selectAll("path").style("stroke", "#406E75").style("stroke-width", "2px").style("stroke-linecap", "square").style("shape-rendering", "crispedges");
  svg.selectAll(".tick").selectAll("text").style("font-size", "10px").style("fill", "#406E75").style("letter-spacing", ".5px");
}
export function updateYAxis(_ref4) {
  var svg = _ref4.svg,
    yScale = _ref4.yScale,
    metadata = _ref4.metadata,
    hideYAxis = _ref4.hideYAxis,
    capYTicks = _ref4.capYTicks,
    margin = _ref4.margin;
  var svgWidth = convertSvgAttribute(svg.style("width"));
  var width = svgWidth - margin.left - margin.right;
  var calleeY = d3.axisRight(yScale).tickSize(0).tickSizeOuter(0).tickPadding(5).tickFormat(function (d, i) {
    return capYTicks && i % 2 !== 0 ? "" : format(metadata.measureUnit, d);
  });
  svg.select(".yAxis").transition().ease(d3.easeQuad).duration(400).attr("transform", "translate(".concat([width + margin.left, margin.top], ")")).call(calleeY);
  if (hideYAxis) {
    d3.selectAll(".yAxis").selectAll("path").style("stroke-width", "1px").style("shape-rendering", "crispedges").style("stroke", "#e8e7e6");
  } else {
    svg.selectAll(".yAxis").selectAll("path").style("stroke", "#406E75").style("stroke-width", "2px").style("stroke-linecap", "square").style("shape-rendering", "crispedges");
    svg.selectAll("yAxis").selectAll("path").style("stroke-linecap", "butt");
    svg.selectAll(".tick").selectAll("text").style("font-size", "10px").style("fill", "#406E75").style("letter-spacing", ".5px");
  }
}
export function updateTimePeriodSurfaces(_ref5) {
  var svg = _ref5.svg,
    xScale = _ref5.xScale,
    periodType = _ref5.periodType,
    cappedTicks = _ref5.cappedTicks,
    measurementPeriods = _ref5.measurementPeriods,
    margin = _ref5.margin,
    changeToSingleDateAndBarchart = _ref5.changeToSingleDateAndBarchart,
    disableGraphInteractions = _ref5.disableGraphInteractions;
  var interactivity = !!changeToSingleDateAndBarchart && !disableGraphInteractions;
  var svgHeight = convertSvgAttribute(svg.style("height"));
  var height = svgHeight - margin.top - margin.bottom;
  var surfaceWidth = 16;
  var tickValues = filterTicks(pluck("end", measurementPeriods), periodType, cappedTicks);
  var x = function x(d) {
    return xScale(d) - surfaceWidth / 2;
  };
  var selection = svg.select(".gridSurfaces").selectAll("rect").data(tickValues);
  var created = selection.enter().append("rect").attr("height", height).attr("width", surfaceWidth).attr("fill", "transparent").attr("cursor", function () {
    return interactivity ? "pointer" : "initial";
  }).attr("transform", function (d) {
    return "translate(".concat([x(d), 0], ")");
  }).on("click", function (d) {
    return interactivity && changeToSingleDateAndBarchart(d);
  }).on("mouseover", function () {
    interactivity && d3.select(this).transition().attr("fill", "#f2f2f2");
  }).on("mouseout", function () {
    interactivity && d3.select(this).transition().attr("fill", "transparent");
  });
  selection.merge(created).attr("height", height);
  selection.attr("transform", function (d) {
    return "translate(".concat([x(d), 0], ")");
  });
  selection.exit().remove();
}
export function updateGridLines(_ref6) {
  var svg = _ref6.svg,
    xScale = _ref6.xScale,
    yScale = _ref6.yScale,
    periodType = _ref6.periodType,
    cappedTicks = _ref6.cappedTicks,
    margin = _ref6.margin,
    measurementPeriods = _ref6.measurementPeriods;
  var svgWidth = convertSvgAttribute(svg.style("width"));
  var svgHeight = convertSvgAttribute(svg.style("height"));
  var width = svgWidth - margin.left - margin.right;
  var height = svgHeight - margin.top - margin.bottom;
  var tickValues = filterTicks(pluck("end", measurementPeriods), periodType, cappedTicks);
  var calleeX = d3.axisBottom(xScale).tickValues(tickValues).tickSize(height).tickFormat("");
  var calleeY = d3.axisRight(yScale).tickSize(width).tickFormat("");
  svg.select(".xGrid").transition().ease(d3.easeQuad).duration(400).attr("transform", "translate(".concat([margin.left, margin.top], ")")).call(calleeX);
  svg.select(".yGrid").transition().ease(d3.easeQuad).duration(400).attr("transform", "translate(".concat([margin.left, margin.top], ")")).call(calleeY);
  svg.select(".xGrid").append("g").attr("class", "tick").append("line").attr("y2", height);
  d3.selectAll(".xGrid").selectAll("path").style("visibility", "hidden");
  d3.selectAll(".yGrid").selectAll("path").style("visibility", "hidden");
  d3.selectAll(".grid").style("fill", "none").style("stroke-width", "1px").style("shape-rendering", "crispedges");
  d3.selectAll(".grid").selectAll("line, path").style("stroke", "#e8e7e6").style("shape-rendering", "crispedges");
}
export function updateDots(_ref7) {
  var content = _ref7.content,
    periodType = _ref7.periodType,
    margin = _ref7.margin,
    measurementsWithMetadata = _ref7.measurementsWithMetadata,
    metadata = _ref7.metadata,
    organizationLookup = _ref7.organizationLookup,
    key = _ref7.key,
    color = _ref7.color,
    setFocusId = _ref7.setFocusId,
    unitlistIdx = _ref7.unitlistIdx,
    xScale = _ref7.xScale,
    yScale = _ref7.yScale,
    description = _ref7.description,
    setDescription = _ref7.setDescription;
  var hydrate = function hydrate(x) {
    return _objectSpread(_objectSpread({}, x), {}, {
      measurements: x.measurements.map(function (y) {
        return _objectSpread(_objectSpread({}, y), {}, {
          organizationId: x.organizationId,
          measureId: x.measureId
        });
      })
    });
  };
  var hydratedData = measurementsWithMetadata.map(hydrate);
  var boundLonely = content.selectAll(".circleGroup").data(hydratedData, key);
  var boundLonelyEnter = boundLonely.enter().append("g").attr("class", "circleGroup").attr("transform", "translate(".concat([margin.left, margin.top], ")"));
  var circleSelection = boundLonely.merge(boundLonelyEnter).style("fill", function (d) {
    var _color2;
    return ((_color2 = color(unitlistIdx(d.organizationId), d.organizationId)) === null || _color2 === void 0 ? void 0 : _color2.hex) || "#ffffff00";
  }).selectAll("circle").data(prop("measurements"));
  boundLonely.exit().remove();
  var value = extractValue(null);
  var visibility = function visibility(d) {
    return typeof d === "number" ? "visible" : "hidden";
  };
  var endPeriod = function endPeriod(d) {
    return d.period.end;
  };
  var dateLabel = translateDateToPeriod(periodType);
  function mouseOver(d) {
    var _metadata$submeasures, _metadata$submeasures2, _color3, _organizationLookup;
    d3.select(this).attr("r", 8).attr("stroke-width", "10px");
    var gender = metadata === null || metadata === void 0 ? void 0 : (_metadata$submeasures = metadata.submeasures) === null || _metadata$submeasures === void 0 ? void 0 : (_metadata$submeasures2 = _metadata$submeasures.find(propEq("measureId", d.measureId))) === null || _metadata$submeasures2 === void 0 ? void 0 : _metadata$submeasures2.gender;
    gender = gender === "Male" ? "Män" : gender === "Female" ? "Kvinnor" : "Totalt";
    setFocusId(null);
    setDescription(format(metadata.measureUnit, value(d)), ((_color3 = color(unitlistIdx(d.organizationId), d.organizationId)) === null || _color3 === void 0 ? void 0 : _color3.hex) || "#ffffff00", (_organizationLookup = organizationLookup(d.organizationId)) === null || _organizationLookup === void 0 ? void 0 : _organizationLookup.name, compose(xScale, endPeriod)(d), compose(yScale, value)(d), compose(dateLabel, endPeriod)(d), gender);
  }
  function mouseLeave() {
    d3.select(this).attr("r", 3).attr("stroke-width", "20px");
    description.transition().attr("opacity", 0);
  }
  circleSelection.enter().append("circle").attr("cx", compose(xScale, endPeriod)).attr("cy", compose(yScale, value)).attr("visibility", compose(visibility, value)).attr("stroke", "transparent").attr("stroke-width", "20px").on("mouseover", mouseOver).on("mouseleave", mouseLeave).attr("r", 0).transition().ease(d3.easeQuad).duration(400).attr("r", 3);
  circleSelection.attr("visibility", compose(visibility, value)).attr("cx", compose(xScale, endPeriod)).attr("cy", compose(yScale, value)).on("mouseover", mouseOver).on("mouseleave", mouseLeave);
  circleSelection.exit().transition().ease(d3.easeQuad).duration(400).attr("r", 0).remove();
}
export function updateSoughtValue(_ref8) {
  var svg = _ref8.svg,
    target = _ref8.target,
    yScale = _ref8.yScale,
    width = _ref8.width,
    margin = _ref8.margin,
    hydratedTargets = _ref8.hydratedTargets;
  var data = target && hydratedTargets || [];
  var padding = 4;
  var selection = svg.selectAll(".hover-description-group").selectAll(".manymeasure-target-value").data(data);
  var created = selection.enter().append("line").attr("transform", "translate(".concat([0, margin.top], ")")).attr("x1", 0).attr("x2", 0).attr("y1", function (d) {
    return yScale(d.value || d.Value);
  }).attr("y2", function (d) {
    return yScale(d.value || d.Value);
  }).attr("class", function (d, idx) {
    return "manymeasure-target-value target".concat(idx);
  }).attr("stroke-width", 3).attr("stroke-dasharray", "3,3").style("stroke", function (d, idx) {
    return targetColor(idx);
  }).style("fill", "none");
  selection.merge(created).transition().ease(d3.easeQuad).duration(400).attr("x1", 0).attr("x2", width - padding).attr("y1", function (d) {
    return yScale(d.value || d.Value);
  }).attr("y2", function (d) {
    return yScale(d.value || d.Value);
  }).attr("transform", "translate(".concat([margin.left, margin.top], ")"));
  selection.exit().transition().ease(d3.easeQuad).duration(400).style("opacity", "0").remove();
}